import { skylinePageForExpansion } from './skylineCityPages.js';
import { PRICING_COPY } from './pricingCopy.js';

/** Contractor / installer / aluminum landing pages for newer city SEO targets. */

const CITY_META = {
  'north-vancouver': {
    name: 'North Vancouver',
    areaServed: 'North Vancouver, British Columbia',
    neighbourhoods: 'Lynn Valley, Lower Lonsdale, and Edgemont',
    localDetail: 'North Shore rain, sloped lots, and raised decks',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Lower Mainland aluminum patio cover — planning reference for North Vancouver sloped lots and raised decks',
      caption:
        'Lower Mainland aluminum patio cover example used for North Vancouver planning — rain-ready protection for sloped yards and raised decks. Compare glass in the online Design & Price tool using the same size.',
      projectPath: '/projects/north-vancouver-aluminum-patio-cover',
    },
    glassCaseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'North Shore glass patio cover — planning reference for North Vancouver raised decks',
      caption:
        'North Shore glass patio cover example used for North Vancouver planning — bright rain protection for raised decks and walkouts. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/north-vancouver-glass-patio-cover',
    },
    glassProjectPath: '/projects/north-vancouver-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for North Vancouver raised decks',
      caption:
        'Skyline combo patio cover example used for North Vancouver planning — balanced light and shade on sloped lots and raised decks. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
  },
  'west-vancouver': {
    name: 'West Vancouver',
    areaServed: 'West Vancouver, British Columbia',
    neighbourhoods: 'Ambleside, Dundarave, and the British Properties',
    localDetail: 'coastal rain, wind exposure, and premium glass options',
    heroImage: '/house/glass/glass-hero.png',
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'West Coast glass patio cover with natural light under tempered glass roof — planning reference for West Vancouver',
      caption:
        'West Coast glass patio cover example used for West Vancouver planning — bright rain protection for view lines and walkout decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/west-vancouver-glass-patio-cover',
    },
    aluminumCaseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'North Shore aluminum patio cover — planning reference for West Vancouver coastal decks',
      caption:
        'North Shore aluminum patio cover example used for West Vancouver planning — practical rain protection for coastal decks. Compare glass for light in the online Design & Price tool.',
      projectPath: '/projects/west-vancouver-aluminum-patio-cover',
    },
    glassProjectPath: '/projects/west-vancouver-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for West Vancouver coastal decks',
      caption:
        'Skyline combo patio cover example used for West Vancouver planning — mixed light and shade for coastal walkouts. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
  },
  'new-westminster': {
    name: 'New Westminster',
    areaServed: 'New Westminster, British Columbia',
    neighbourhoods: 'Queensborough, Sapperton, and Uptown',
    localDetail: 'townhomes and detached homes near the riverfront',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Lower Mainland aluminum patio cover on a compact backyard — planning reference for New Westminster',
      caption:
        'Lower Mainland aluminum patio cover example used for New Westminster planning — practical for townhome patios and detached backyards. Use our Design & Price tool to see a planning range for your dimensions.',
      projectPath: '/projects/new-westminster-aluminum-patio-cover',
    },
    glassCaseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Lower Mainland glass patio cover — planning reference for New Westminster townhome and riverfront decks',
      caption:
        'Lower Mainland glass patio cover example used for New Westminster planning — bright rain protection for compact decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/new-westminster-glass-patio-cover',
    },
    glassProjectPath: '/projects/new-westminster-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/skyline/p40.jpg',
      alt: 'Skyline combo patio cover — planning reference for New Westminster townhome and detached patios',
      caption:
        'Skyline combo patio cover example used for New Westminster planning — balanced light and shade on compact lots. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/new-westminster-skyline-combo-cover',
    },
  },
  'maple-ridge': {
    name: 'Maple Ridge',
    areaServed: 'Maple Ridge, British Columbia',
    neighbourhoods: 'Town Centre, Albion, and Silver Valley',
    localDetail: 'larger lots and family backyards',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Fraser Valley skyline combo patio cover on a larger suburban backyard — planning reference for Maple Ridge',
      caption:
        'Fraser Valley skyline combo example used for Maple Ridge planning — larger backyard layouts with mixed light and shade. Compare aluminum and glass on your dimensions in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
    glassCaseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Fraser Valley glass patio cover — planning reference for Maple Ridge family backyards',
      caption:
        'Fraser Valley glass patio cover example used for Maple Ridge planning — bright rain protection on larger lots. Compare aluminum and combo in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-glass-patio-cover',
    },
    glassProjectPath: '/projects/maple-ridge-glass-patio-cover',
    aluminumCaseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Fraser Valley aluminum patio cover — planning reference for Maple Ridge family backyards',
      caption:
        'Fraser Valley aluminum patio cover example used for Maple Ridge planning — rain-ready coverage for larger lots. Compare glass and combo in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-aluminum-patio-cover',
    },
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Fraser Valley skyline combo patio cover — planning reference for Maple Ridge lots',
      caption:
        'Fraser Valley skyline combo example used for Maple Ridge planning — mixed light and shade on larger backyard layouts. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
  },
  'pitt-meadows': {
    name: 'Pitt Meadows',
    areaServed: 'Pitt Meadows, British Columbia',
    neighbourhoods: 'Pitt Meadows and nearby Maple Ridge',
    localDetail: 'wider spans and Fraser Valley weather',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Lower Mainland aluminum patio cover — planning reference for wider Pitt Meadows backyard spans',
      caption:
        'Lower Mainland aluminum patio cover example used for Pitt Meadows planning — practical rain protection for wider Fraser Valley spans. Online price before free measurement.',
      projectPath: '/projects/pitt-meadows-aluminum-patio-cover',
    },
    glassCaseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Fraser Valley glass patio cover — planning reference for wider Pitt Meadows backyard spans',
      caption:
        'Fraser Valley glass patio cover example used for Pitt Meadows planning — bright rain protection on wider spans. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/pitt-meadows-glass-patio-cover',
    },
    glassProjectPath: '/projects/pitt-meadows-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for wider Pitt Meadows backyard spans',
      caption:
        'Skyline combo patio cover example used for Pitt Meadows planning — mixed light and shade on wider Fraser Valley spans. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
  },
  'port-coquitlam': {
    name: 'Port Coquitlam',
    areaServed: 'Port Coquitlam, British Columbia',
    neighbourhoods: 'Citadel Heights, Mary Hill, and Lincoln Park',
    localDetail: 'Tri-Cities rain, townhomes, and hillside yards',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Tri-Cities glass patio cover — planning reference for Port Coquitlam yards and hillside decks',
      caption:
        'Tri-Cities glass patio cover example used for Port Coquitlam planning — rain-ready option with more natural light. Compare aluminum on the same size in the online Design & Price tool.',
      projectPath: '/projects/port-coquitlam-glass-patio-cover',
    },
    aluminumCaseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Tri-Cities aluminum patio cover — planning reference for Port Coquitlam townhomes and hillside yards',
      caption:
        'Tri-Cities aluminum patio cover example used for Port Coquitlam planning — practical rain protection for townhomes and hillside yards. Compare glass for light in the online Design & Price tool.',
      projectPath: '/projects/port-coquitlam-aluminum-patio-cover',
    },
    glassProjectPath: '/projects/port-coquitlam-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for Port Coquitlam townhomes and hillside yards',
      caption:
        'Skyline combo patio cover example used for Port Coquitlam planning — balanced light and shade for Tri-Cities yards. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
  },
  'port-moody': {
    name: 'Port Moody',
    areaServed: 'Port Moody, British Columbia',
    neighbourhoods: 'Moody Centre, Suter Brook, and Newport Village',
    localDetail: 'coastal rain, compact lots, and view-oriented decks',
    heroImage: '/house/glass/glass-hero.png',
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Tri-Cities glass patio cover on a compact backyard — planning reference for Port Moody walkouts',
      caption:
        'Tri-Cities glass patio cover example used for Port Moody planning — bright rain protection for compact walkout decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/port-moody-glass-patio-cover',
    },
    aluminumCaseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Tri-Cities aluminum patio cover — planning reference for Port Moody compact decks',
      caption:
        'Tri-Cities aluminum patio cover example used for Port Moody planning — practical rain protection for compact coastal decks. Compare glass for light in the online Design & Price tool.',
      projectPath: '/projects/port-moody-aluminum-patio-cover',
    },
    glassProjectPath: '/projects/port-moody-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for Port Moody compact walkout decks',
      caption:
        'Skyline combo patio cover example used for Port Moody planning — mixed light and shade on compact coastal decks. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
  },
  abbotsford: {
    name: 'Abbotsford',
    areaServed: 'Abbotsford, British Columbia',
    neighbourhoods: 'Downtown Abbotsford, Clearbrook, and Sumas Mountain',
    localDetail: 'Fraser Valley rain, larger lots, and family backyards',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Fraser Valley aluminum patio cover — planning reference for Abbotsford family backyards',
      caption:
        'Fraser Valley aluminum patio cover example used for Abbotsford planning — rain-ready coverage for larger family lots. Compare glass and combo on your dimensions in the online Design & Price tool.',
      projectPath: '/projects/abbotsford-aluminum-patio-cover',
    },
    glassCaseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Fraser Valley glass patio cover — planning reference for Abbotsford family backyards',
      caption:
        'Fraser Valley glass patio cover example used for Abbotsford planning — bright rain protection for larger family lots. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/abbotsford-glass-patio-cover',
    },
    glassProjectPath: '/projects/abbotsford-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for Abbotsford family backyards',
      caption:
        'Skyline combo patio cover example used for Abbotsford planning — balanced light and shade on larger Fraser Valley lots. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
  },
  'white-rock': {
    name: 'White Rock',
    areaServed: 'White Rock, British Columbia',
    neighbourhoods: 'East Beach, West Beach, and nearby South Surrey',
    localDetail: 'coastal wind, ocean exposure, and compact seaside lots',
    heroImage: '/house/glass/glass-hero.png',
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Coastal Metro Vancouver glass patio cover — planning reference for White Rock and South Surrey decks',
      caption:
        'Coastal glass patio cover example used for White Rock planning — bright rain protection for seaside decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/white-rock-glass-patio-cover',
    },
    aluminumCaseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Coastal aluminum patio cover — planning reference for White Rock and South Surrey decks',
      caption:
        'Coastal aluminum patio cover example used for White Rock planning — practical rain protection for seaside decks. Compare glass for light in the online Design & Price tool.',
      projectPath: '/projects/white-rock-aluminum-patio-cover',
    },
    glassProjectPath: '/projects/white-rock-glass-patio-cover',
    skylineCaseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover — planning reference for White Rock and South Surrey decks',
      caption:
        'Skyline combo patio cover example used for White Rock planning — mixed light and shade for coastal decks. Compare aluminum and glass in the online Design & Price tool.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
  },
};

function contractorPage(slug, meta) {
  return {
    id: `contractor-${slug}`,
    path: `/patio-cover-contractor-${slug}`,
    heroImage: meta.heroImage,
    metaTitle: `Patio Cover Contractor ${meta.name} | LoomiHome Patios`,
    metaDescription:
      `Looking for a patio cover contractor in ${meta.name}? Compare aluminum, glass, and combo patio covers online. Sunroom pricing is confirmed after a free on-site measurement of the buildable area.`,
    h1: `Patio Cover Contractor in ${meta.name}`,
    serviceType: 'Patio cover contractor',
    areaServed: meta.areaServed,
    intro:
      `If you are comparing patio cover contractors in ${meta.name}, start with a clear budget range. LoomiHome helps homeowners in ${meta.neighbourhoods} compare cover types online before booking a free on-site measurement.`,
    highlights: [
      `Online patio cover planning range for ${meta.name} homes`,
      'Compare aluminum, glass, skyline combo, and sunroom options',
      `Helpful for ${meta.neighbourhoods}`,
      'Free on-site measurement for final contractor pricing',
    ],
    localAngle:
      `${meta.name} projects often involve ${meta.localDetail}. A rough online estimate saves time before a contractor visit and helps you decide whether aluminum, glass, or a combo cover fits the lot.`,
    sections: [
      {
        h2: `What a ${meta.name} contractor quote should clarify`,
        body:
          `A useful ${meta.name} quote should explain product direction, approximate size, attachment, drainage, and what still needs to be confirmed on site — especially where ${meta.localDetail} affects posts and roof slope.`,
      },
      {
        h2: 'Why start online before booking visits',
        body:
          'A ballpark helps you avoid booking multiple contractor appointments before you know whether the project fits your budget. Share neighbourhood, rough dimensions, and preferred cover type in the online Design & Price tool.',
      },
      {
        h2: `Local factors in ${meta.name}`,
        body:
          `${meta.neighbourhoods} each bring different patio layouts. We use your photos and size notes first, then confirm attachment height, drainage direction, and access during the free measurement.`,
      },
    ],
    pricingNote:
      `Share your ${meta.name} neighbourhood, rough dimensions, and preferred cover type for a starting range. See the ${meta.name} cost guide for planning totals on common sizes.`,
    faqs: [
      {
        q: `Do you provide patio cover contractor quotes in ${meta.name}?`,
        a: `Yes. Start with a planning range from our Design & Price tool for ${meta.name}, then book a free on-site measurement if the range works.`,
      },
      {
        q: `How much does a patio cover cost in ${meta.name}?`,
        a: `Our Design & Price tool gives a planning total for your size. For local cost factors and local notes, see our ${meta.name} cost guide before you book measurement.`,
      },
      {
        q: 'What patio cover types can I compare?',
        a: 'You can compare aluminum, glass, skyline combo, and sunroom directions before choosing what to measure.',
      },
      {
        q: `Is the online ${meta.name} contractor quote final?`,
        a: 'No — our Design & Price tool gives a planning range only. Your formal quote is confirmed after free on-site measurement.',
      },
      {
        q: `Which ${meta.name} areas do you quote?`,
        a: `${meta.neighbourhoods} and surrounding neighbourhoods — enter your city and approximate size in the online Design & Price tool to see a planning range.`,
      },
    ],
    relatedPageLinks: [
      { path: `/patio-covers-${slug}`, label: `Patio covers in ${meta.name}` },
      { path: `/patio-cover-cost-${slug}`, label: `Patio cover cost in ${meta.name}` },
      { path: `/aluminum-patio-covers-${slug}`, label: `Aluminum patio covers in ${meta.name}` },
      { path: `/glass-patio-covers-${slug}`, label: `Glass patio covers in ${meta.name}` },
    ],
    ...(meta.caseStudy ? { caseStudy: meta.caseStudy } : {}),
  };
}

const SUNROOM_CASE_STUDY = {
  image: '/house/realprojects/sunroom-white.png',
  alt: 'White-framed sunroom enclosure with glass walls on a Lower Mainland home',
  caption:
    'For open patio covers, use the online Design & Price tool for a planning range. Sunroom pricing is confirmed after a free on-site measurement of the buildable area.',
};

function installerPage(slug, meta) {
  return {
    id: `installer-${slug}`,
    path: `/patio-cover-installer-${slug}`,
    heroImage: meta.heroImage,
    metaTitle: `Patio Cover Installer ${meta.name} | LoomiHome Patios`,
    metaDescription:
      `Patio cover installer in ${meta.name} — aluminum, glass, combo covers, and sunrooms. Free on-site measurement to review buildable area and confirm sunroom pricing.`,
    h1: `Patio Cover Installer in ${meta.name}`,
    serviceType: 'Patio cover installation',
    areaServed: meta.areaServed,
    intro:
      `${meta.name} homeowners searching for a patio cover installer often want practical answers first: what type fits, what it might cost, and how it will handle local weather around ${meta.neighbourhoods}. We start with a rough estimate, then confirm details during a free measurement.`,
    highlights: [
      `Patio cover installation options across ${meta.name}`,
      'Compare aluminum, glass, combo, and sunroom paths',
      `Helpful for ${meta.neighbourhoods}`,
      'Online patio cover planning range followed by free measurement',
    ],
    localAngle:
      `${meta.name} sites vary in ${meta.localDetail}. Attachment, slope, and drainage are confirmed during the free site visit.`,
    sections: [
      {
        h2: `Installation process in ${meta.name}`,
        body:
          `Post placement, wall or deck attachment, roof slope, gutter direction, access, and exposure all shape a proper ${meta.name} patio cover installation and the final quote — especially where ${meta.localDetail} affects the layout.`,
      },
      {
        h2: 'Estimate first, install planning second',
        body:
          'The first estimate helps you decide whether the project is worth measuring. The site visit handles practical installation details and confirms pricing.',
      },
      {
        h2: `What ${meta.name} homeowners should prepare`,
        body:
          'Rough patio size, a few photos, and your neighbourhood help us ballpark product direction before anyone visits. Formal install timing is confirmed after measurement.',
      },
    ],
    pricingNote:
      `Share rough dimensions and one or two photos. We can ballpark the likely product direction before a ${meta.name} site visit — see the ${meta.name} cost guide for typical totals.`,
    faqs: [
      {
        q: `Do you install patio covers in ${meta.name}?`,
        a: `Yes, we serve ${meta.name} as part of our Metro Vancouver and Lower Mainland coverage, including ${meta.neighbourhoods}.`,
      },
      {
        q: `How much does patio cover installation cost in ${meta.name}?`,
        a: `Our Design & Price tool gives a planning total for your size. See the ${meta.name} patio cover cost guide for common ranges, then book free measurement for a formal quote.`,
      },
      {
        q: `How long does patio cover installation take in ${meta.name}?`,
        a: 'Most installs finish within a few days once materials are ready. We confirm a realistic schedule after measurement.',
      },
      {
        q: `Is the online ${meta.name} installation estimate final?`,
        a: 'No — our Design & Price tool gives a planning range only. Final installation pricing is confirmed after free on-site measurement.',
      },
    ],
    relatedPageLinks: [
      { path: `/patio-covers-${slug}`, label: `Patio covers in ${meta.name}` },
      { path: `/patio-cover-cost-${slug}`, label: `Patio cover cost in ${meta.name}` },
      { path: `/patio-cover-contractor-${slug}`, label: `Patio cover contractor in ${meta.name}` },
      { path: `/aluminum-patio-covers-${slug}`, label: `Aluminum patio covers in ${meta.name}` },
    ],
    ...(meta.caseStudy ? { caseStudy: meta.caseStudy } : {}),
  };
}

function aluminumPage(slug, meta) {
  return {
    id: `aluminum-${slug}`,
    path: `/aluminum-patio-covers-${slug}`,
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: `Aluminum Patio Covers ${meta.name} | LoomiHome Patios`,
    metaDescription:
      `Aluminum patio covers in ${meta.name} — rain-ready, low-maintenance backyard covers. Get a fast rough estimate, compare options, then book a free measurement.`,
    h1: `Aluminum Patio Covers in ${meta.name}`,
    serviceType: 'Aluminum patio cover installation',
    areaServed: meta.areaServed,
    intro:
      `Aluminum patio covers are a practical choice for ${meta.name} homes that need dependable rain protection without heavy upkeep — especially around ${meta.neighbourhoods}. Start with a fast rough estimate, then book a free measurement when the range fits your budget.`,
    highlights: [
      `Low-maintenance aluminum covers for ${meta.name}`,
      'Strong rain protection for Metro Vancouver seasons',
      `Helpful for ${meta.neighbourhoods}`,
      'Free on-site measurement after the initial ballpark',
    ],
    localAngle:
      `${meta.name} patios often need to handle ${meta.localDetail}. Aluminum gives a clean, durable roof before you decide on glass or combo upgrades.`,
    sections: [
      {
        h2: `Why aluminum works well in ${meta.name}`,
        body:
          `Aluminum covers are usually the first option when ${meta.name} homeowners want reliable rain coverage, straightforward maintenance, and a clear budget range — without committing to glass pricing yet.`,
      },
      {
        h2: 'What we confirm on site',
        body:
          'Width, projection, post placement, attachment, and drainage are measured during the free visit so the final quote matches your home.',
      },
      {
        h2: `Local layout notes for ${meta.name}`,
        body:
          `${meta.localDetail.charAt(0).toUpperCase() + meta.localDetail.slice(1)} can change post placement and roof slope. Photos and rough dimensions help us discuss post placement and roof slope before the visit; use the online Design & Price tool for a planning range.`,
      },
    ],
    pricingNote:
      `Enter approximate patio dimensions in our Design & Price tool to see an aluminum price before anyone visits your ${meta.name} home. Compare with the ${meta.name} cost guide for common sizes.`,
    faqs: [
      {
        q: `Do you install aluminum patio covers in ${meta.name}?`,
        a: `Yes — ${meta.name} is part of our regular Lower Mainland service area, including ${meta.neighbourhoods}.`,
      },
      {
        q: `How much does an aluminum patio cover cost in ${meta.name}?`,
        a: `Our Design & Price tool gives a planning total for your size. See the ${meta.name} patio cover cost guide for local cost factors, then book free measurement for a formal quote.`,
      },
      {
        q: 'Can I compare aluminum with glass or a sunroom?',
        a: 'Yes. The online Design & Price tool compares multiple product directions using the same rough dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: `/patio-covers-${slug}`, label: `Patio covers in ${meta.name}` },
      { path: `/patio-cover-cost-${slug}`, label: `Patio cover cost in ${meta.name}` },
      { path: `/patio-cover-contractor-${slug}`, label: `Patio cover contractor in ${meta.name}` },
      { path: `/glass-patio-covers-${slug}`, label: `Glass patio covers in ${meta.name}` },
    ],
    ...(meta.aluminumCaseStudy
      ? { caseStudy: meta.aluminumCaseStudy }
      : meta.caseStudy
        ? { caseStudy: meta.caseStudy }
        : {}),
  };
}

function glassPage(slug, meta) {
  return {
    id: `glass-${slug}`,
    path: `/glass-patio-covers-${slug}`,
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: `Glass Patio Covers ${meta.name} | LoomiHome Patios`,
    metaDescription:
      `Glass patio covers in ${meta.name} — bright, modern rain protection that keeps decks full of natural light. Fast estimate and free measurement.`,
    h1: `Glass Patio Covers in ${meta.name}`,
    serviceType: 'Glass patio cover installation',
    areaServed: meta.areaServed,
    intro:
      `Glass patio covers are worth comparing in ${meta.name} when you want rain protection but still want the deck and back of the home to stay bright — a common request around ${meta.neighbourhoods}.`,
    highlights: [
      `Bright overhead rain protection for ${meta.name} decks`,
      'Modern glass and black-frame design direction',
      `Helpful for ${meta.neighbourhoods}`,
      'Free on-site measurement to review buildable area and site conditions',
    ],
    localAngle:
      `${meta.name} homes with walkout decks and large rear windows often compare glass to keep natural light. ${meta.localDetail.charAt(0).toUpperCase() + meta.localDetail.slice(1)} — slope and drainage are confirmed on site.`,
    sections: [
      {
        h2: `When glass is worth the upgrade in ${meta.name}`,
        body:
          `Glass usually costs more than a basic aluminum cover, but it keeps ${meta.name} decks and adjoining rooms brighter and can look more premium from inside the home — especially on walkouts where light matters.`,
      },
      {
        h2: 'What affects glass pricing',
        body:
          'Panel layout, span, support posts, attachment height, glass type, and drainage direction all affect the final quote. A quick estimate helps you decide before measuring.',
      },
      {
        h2: `Local notes for ${meta.name}`,
        body:
          `${meta.localDetail.charAt(0).toUpperCase() + meta.localDetail.slice(1)} can change frame height and drainage direction. Share neighbourhood and rough dimensions in chat before booking a free measurement.`,
      },
    ],
    pricingNote:
      `Send rough dimensions and whether you want maximum light or a balanced mix of shade. Compare glass and aluminum from the same measurements, and see the ${meta.name} cost guide for typical totals.`,
    faqs: [
      {
        q: `Are glass patio covers good for ${meta.name} homes?`,
        a: `Yes, especially around ${meta.neighbourhoods} where keeping natural light matters. Slope and drainage are confirmed on site.`,
      },
      {
        q: `How much does a glass patio cover cost in ${meta.name}?`,
        a: `Our Design & Price tool gives a planning total for your size. See the ${meta.name} patio cover cost guide for local glass cost factors, then book free measurement for a formal quote.`,
      },
      {
        q: 'Can I compare glass and aluminum pricing?',
        a: 'Yes. The online Design & Price tool compares both options using the same approximate dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: `/patio-covers-${slug}`, label: `Patio covers in ${meta.name}` },
      { path: `/patio-cover-cost-${slug}`, label: `Patio cover cost in ${meta.name}` },
      { path: `/patio-cover-contractor-${slug}`, label: `Patio cover contractor in ${meta.name}` },
      { path: `/aluminum-patio-covers-${slug}`, label: `Aluminum patio covers in ${meta.name}` },
      ...(meta.glassProjectPath
        ? [{ path: meta.glassProjectPath, label: `${meta.name} glass project` }]
        : []),
    ],
    ...(meta.glassCaseStudy
      ? { caseStudy: meta.glassCaseStudy }
      : meta.caseStudy
        ? { caseStudy: meta.caseStudy }
        : {}),
  };
}

function sunroomPage(slug, meta) {
  return {
    id: `sunrooms-${slug}`,
    path: `/sunrooms-${slug}`,
    heroImage: '/house/sunrooms/sunroom-hero.png',
    metaTitle: `Sunrooms ${meta.name} | LoomiHome Patios`,
    metaDescription:
      `Sunrooms in ${meta.name} — enclosed patio and glass room options for brighter, year-round usable space. Free on-site measurement to review buildable area and confirm sunroom pricing.`,
    h1: `Sunrooms in ${meta.name}`,
    serviceType: 'Sunroom installation',
    areaServed: meta.areaServed,
    intro:
      `${meta.name} homeowners comparing sunrooms often want to know whether the buildable area and layout will work. We review those details during a free on-site measurement before confirming pricing.`,
    highlights: [
      `Enclosed patio and sunroom options in ${meta.name}`,
      'Compare sunroom budget against open patio covers',
      `Helpful for ${meta.neighbourhoods}`,
      'Free on-site measurement to review buildable area and site conditions',
    ],
    localAngle:
      `${meta.name} lots vary in size and layout — ${meta.localDetail}. Existing deck condition, attachment, and drainage are reviewed before final sunroom pricing.`,
    sections: [
      {
        h2: `When to consider a sunroom in ${meta.name}`,
        body:
          `Choose a sunroom when you want more enclosure and comfort than a roof alone provides. In ${meta.name}, it works well for sitting areas, hobby rooms, or a protected transition to the backyard.`,
      },
      {
        h2: 'Budget before design',
        body:
          'Sunroom pricing depends on the buildable area, layout, and site conditions. We confirm the design and price after a free on-site measurement.',
      },
      {
        h2: `Local layout factors in ${meta.name}`,
        body:
          `${meta.localDetail.charAt(0).toUpperCase() + meta.localDetail.slice(1)} can change footprint and attachment options. Photos and approximate size in chat help compare a sunroom against a simpler patio cover.`,
      },
    ],
    pricingNote:
      `Share the approximate footprint and a few photos. We can compare a sunroom with simpler patio cover options if budget is uncertain — see the ${meta.name} cost guide for open-cover ranges too.`,
    faqs: [
      {
        q: `Do you build sunrooms in ${meta.name}?`,
        a: `Yes — ${meta.name} is part of our Lower Mainland service area, including ${meta.neighbourhoods}.`,
      },
      {
        q: `How much does a sunroom cost in ${meta.name}?`,
        a: 'Sunroom pricing depends on buildable area, layout, and site conditions. We confirm it after a free on-site measurement.',
      },
      {
        q: 'Should I choose a sunroom or a patio cover?',
        a: 'If you mainly need rain protection, start with a patio cover. If you want more enclosure and year-round comfort, compare a sunroom.',
      },
      {
        q: 'How is sunroom pricing confirmed?',
        a: PRICING_COPY.sunroomWallOnly,
      },
    ],
    relatedPageLinks: [
      { path: `/patio-covers-${slug}`, label: `Patio covers in ${meta.name}` },
      { path: `/patio-cover-cost-${slug}`, label: `Patio cover cost in ${meta.name}` },
      { path: `/patio-cover-contractor-${slug}`, label: `Patio cover contractor in ${meta.name}` },
      { path: `/glass-patio-covers-${slug}`, label: `Glass patio covers in ${meta.name}` },
    ],
    caseStudy: SUNROOM_CASE_STUDY,
  };
}

export const CITY_SERVICE_EXPANSION_ORDER = Object.keys(CITY_META).flatMap((slug) => [
  `contractor-${slug}`,
  `installer-${slug}`,
  `aluminum-${slug}`,
  `glass-${slug}`,
  `skyline-${slug}`,
  `sunrooms-${slug}`,
]);

export const CITY_SERVICE_EXPANSION_PAGES = Object.fromEntries(
  Object.entries(CITY_META).flatMap(([slug, meta]) => [
    [`contractor-${slug}`, contractorPage(slug, meta)],
    [`installer-${slug}`, installerPage(slug, meta)],
    [`aluminum-${slug}`, aluminumPage(slug, meta)],
    [`glass-${slug}`, glassPage(slug, meta)],
    [`skyline-${slug}`, skylinePageForExpansion(slug, meta)],
    [`sunrooms-${slug}`, sunroomPage(slug, meta)],
  ]),
);
